import React, { createContext, useState, useContext, useEffect } from 'react';

const CartContext = createContext();
export const useCart = () => useContext(CartContext);

const STORAGE_KEY = 'folex_tech_cart';

const uid = () =>
  `${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;

const load = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const items = raw ? JSON.parse(raw) : [];
    if (!Array.isArray(items)) return [];
    // Repair persisted carts: guarantee every line has a unique id. Older
    // carts (and bundles added before the id fix) could contain missing or
    // duplicate ids, which makes React reuse the wrong row on delete and
    // breaks the cart UI.
    const seen = new Set();
    return items.map((it) => {
      let id = it && it.id;
      if (!id || seen.has(id)) id = `${it?.productId || 'item'}_${uid()}`;
      seen.add(id);
      return { ...it, id };
    });
  } catch {
    return [];
  }
};

const save = (items) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  } catch {}
};

const totals = (items) => ({
  count: items.reduce((s, i) => s + i.quantity, 0),
  total: items.reduce((s, i) => s + (i.discountPrice || i.price || 0) * i.quantity, 0),
});

export const CartProvider = ({ children }) => {
  const [cart, setCartState] = useState(load);

  const setCart = (items) => {
    setCartState(items);
    save(items);
  };

  const { count: cartCount, total: cartTotal } = totals(cart);

  const addToCart = (productId, size, color, quantity = 1, productData = null) => {
    setCartState(prev => {
      const idx = prev.findIndex(
        i => i.productId === productId && i.size === size && i.color === color
      );
      let next;
      if (idx > -1) {
        next = prev.map((i, n) =>
          n === idx ? { ...i, quantity: i.quantity + quantity } : i
        );
      } else {
        const item = {
          // Random suffix guarantees a unique key even when a bundle adds
          // several items in the same millisecond.
          id: `${productId}_${size}_${color}_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
          productId,
          size,
          color,
          quantity,
          name: productData?.name || '',
          brand: productData?.brand || '',
          price: productData?.price || 0,
          discountPrice: productData?.discountPrice || productData?.discount_price || null,
          imageUrl: productData?.images?.[0] || productData?.imageUrl || productData?.image_url || '',
          category: productData?.category || '',
          stock: productData?.stock ?? 99,
          // Combo/bundle tagging (set when added from a "Picked for You" look)
          comboId: productData?.comboId || null,
          comboTitle: productData?.comboTitle || null,
          addedAt: new Date().toISOString(),
        };
        next = [...prev, item];
      }
      save(next);
      return next;
    });
    return { success: true };
  };

  const removeFromCart = (itemId) => {
    // Functional update — never relies on a possibly-stale `cart` closure
    // (important right after a bundle adds several items at once).
    setCartState(prev => {
      const next = prev.filter(i => i.id !== itemId);
      save(next);
      return next;
    });
    return { success: true };
  };

  const updateQuantity = (itemId, newQty) => {
    if (newQty < 1) return { success: false };
    setCartState(prev => {
      const next = prev.map(i => i.id === itemId ? { ...i, quantity: newQty } : i);
      save(next);
      return next;
    });
    return { success: true };
  };

  const clearCart = () => {
    setCart([]);
    return { success: true };
  };

  const refreshCart = () => {
    setCartState(load());
  };

  const value = {
    cart,
    cartCount,
    cartTotal,
    loading: false,
    isGuest: false,
    addToCart,
    removeFromCart,
    updateQuantity,
    clearCart,
    refreshCart,
    getGuestId: () => null,
    mergeCarts: () => Promise.resolve({ success: true }),
    userId: null,
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};
